import { createFileRoute, Link } from "@tanstack/react-router";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";

const slug = "open-source-image-models-2026";
const path = `/blog/${slug}`;

export const Route = createFileRoute(path)({
	component: Article,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({ matches, internalPath: path });
		return {
			meta: [
				{
					title:
						"Best Open-Source Image Models for Agent Workflows in 2026 - AgentFramer",
				},
				{
					name: "description",
					content:
						"FLUX, SDXL, Playground, SD3.5, and friends. Picking the right open-source image model for autonomous agent use in 2026.",
				},
				{
					property: "og:title",
					content: "Best Open-Source Image Models for Agent Workflows in 2026",
				},
				{
					property: "og:description",
					content:
						"The open-source image models that hold up under agent workloads.",
				},
				{ property: "og:image", content: ogImageUrl(`blog-${slug}`) },
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "article" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl(`blog-${slug}`) },
			],
			links: buildLocalizedSeoLinks({ matches, internalPath: path }),
		};
	},
});

function Article() {
	return (
		<article className="space-y-10">
			<div className="space-y-2">
				<p className="text-xs text-muted-foreground/60">April 2026</p>
				<h1 className="text-2xl font-medium tracking-tight">
					Best open-source image models for agent workflows in 2026
				</h1>
				<p className="text-sm text-muted-foreground">
					Real specs, real VRAM, real break-even math. The open weights that
					hold up when an agent calls them a thousand times a day, unattended.
				</p>
			</div>

			<div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
				<p>
					An agent has no taste. It cannot squint at a malformed hand and
					regenerate. It cannot decide that today the rim light is wrong. So the
					model picked for an agent loop has to be steady, not just impressive
					in cherry-picked demos. In 2026 that constraint narrows the
					open-source field to about eight models worth shipping, and rules out
					a lot of the leaderboard chatter.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					The 2026 lineup, with specs
				</h2>
				<p>
					Five families cover the practical work: FLUX, SDXL (and its distilled
					turbo variants), Stable Diffusion 3.5, Playground v3, and the smaller
					HiDream family. Below is the spec sheet you actually need before
					deciding what runs where.
				</p>

				<pre className="rounded-md bg-card p-4 text-xs text-foreground overflow-x-auto">{`Model              License        VRAM (FP16)   Quantized    Steps   H100 latency   Notes
FLUX 1.1 pro       Closed         hosted only   —            —       ~2-3s          API only, no weights
FLUX dev           Non-commercial 24 GB         12 GB (NF4)  20-28   5-7s           Best open quality
FLUX schnell       Apache 2.0     24 GB ideal   12 GB (FP8)  4       1-2s           Distilled, agent default
SDXL Lightning     OpenRAIL-M     12 GB         8 GB         2-4     0.4-0.8s       Sub-second, huge LoRA pool
SDXL Turbo         OpenRAIL-M     8 GB          6 GB         1-4     0.3-0.6s       Smallest practical card
SD3.5 Large        SAI Community  16-24 GB      12 GB        28      4-6s           Permissive commercial use
SD3.5 Medium       SAI Community  8-12 GB       6 GB         20-28   2-3s           CPU-friendly, edge OK
Playground v3      Open weights   16 GB         10 GB        25-35   3-5s           Stylized, brand work
HiDream            Open weights   10-12 GB      8 GB         15-20   1-2s           Compact, fast, decent`}</pre>

				<p>
					Two notes on that table. First, FLUX 1.1 pro is closed-source — the
					weights are not released — so it shows up on AgentFramer alongside
					Ideogram v2 and Imagen 3 as a hosted-only option. The other FLUX tiers
					are open. Second, "VRAM (FP16)" is the comfortable headroom number for
					batch size 1 with KV-style attention; the quantized column is what
					works on a 12 GB consumer card with FP8 or NF4 weights and a tolerable
					quality hit (usually a percent or two on CLIP score, more on fine
					detail).
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Schedulers and samplers that actually matter
				</h2>
				<p>
					For FLUX dev, use the Euler sampler with the default flow-matching
					schedule and 20–28 steps. For schnell, four steps with Euler is what
					it was distilled for; do not exceed eight or quality regresses. SDXL
					Lightning ships with a custom 2/4/8-step LCM-style scheduler — use the
					matched checkpoint, not generic LCM. SD3.5 Large prefers the DPM++ 2M
					Karras scheduler at 28 steps; Medium tolerates UniPC at 20. Playground
					v3 is closest to SDXL behavior; EDM2 with 30 steps is the safe
					default. HiDream is fine on Euler ancestral at 16 steps. None of these
					need anyone to invent a new sampler.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Self-host stack: ComfyUI, Diffusers, Replicate-style
				</h2>
				<p>
					Three patterns dominate self-hosting. ComfyUI is the workflow graph:
					nodes wired together, JSON-serializable, easy to version. Diffusers is
					the Python library route — write code, run on a fleet, ship as a
					microservice. Replicate-style cog-image infrastructure is the third
					path: a thin container around weights, deployed to a GPU autoscaler
					you operate yourself. ComfyUI wins for prompt/LoRA iteration,
					Diffusers wins for programmatic control, the autoscaler-style stack
					wins when traffic is bursty.
				</p>

				<p>
					A minimal ComfyUI workflow stub for FLUX schnell on a 4090 looks like
					this:
				</p>

				<pre className="rounded-md bg-card p-4 text-xs text-foreground overflow-x-auto">{`{
  "1": { "class_type": "UNETLoader", "inputs": {
    "unet_name": "flux1-schnell-fp8.safetensors", "weight_dtype": "fp8_e4m3fn"
  }},
  "2": { "class_type": "DualCLIPLoader", "inputs": {
    "clip_name1": "t5xxl_fp8_e4m3fn.safetensors", "clip_name2": "clip_l.safetensors",
    "type": "flux"
  }},
  "3": { "class_type": "VAELoader", "inputs": { "vae_name": "ae.safetensors" }},
  "4": { "class_type": "CLIPTextEncode", "inputs": {
    "text": "{{prompt}}", "clip": ["2", 0]
  }},
  "5": { "class_type": "EmptySD3LatentImage", "inputs": {
    "width": 1024, "height": 1024, "batch_size": 1
  }},
  "6": { "class_type": "KSampler", "inputs": {
    "seed": 0, "steps": 4, "cfg": 1.0, "sampler_name": "euler",
    "scheduler": "simple", "denoise": 1.0,
    "model": ["1", 0], "positive": ["4", 0], "negative": ["4", 0], "latent_image": ["5", 0]
  }},
  "7": { "class_type": "VAEDecode", "inputs": { "samples": ["6", 0], "vae": ["3", 0] }},
  "8": { "class_type": "SaveImage", "inputs": { "filename_prefix": "out", "images": ["7", 0] }}
}`}</pre>

				<p>
					That graph runs schnell at FP8 on a 4090 in roughly 1.6 seconds per
					image. Swapping to FLUX dev at NF4 with 24 steps takes the same card
					to about 9 seconds. On an H100 those numbers compress to 1.0s and 5.5s
					respectively, which is the price point that actually justifies cloud
					rental for a busy agent.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					GPU rental break-even
				</h2>
				<p>
					Open-source weights do not mean free pixels. Cloud GPU pricing in 2026
					settles around $0.30/hr for an RTX 4090, $1.40/hr for an A100 80GB,
					and $2.50/hr for an H100. Translate that to per-image cost and compare
					to AgentFramer's hosted FLUX schnell rate of roughly $0.003/image and
					FLUX dev at $0.025/image.
				</p>

				<pre className="rounded-md bg-card p-4 text-xs text-foreground overflow-x-auto">{`Model           Card     Latency   Images/hr (1x)   GPU $/hr   $/image   Hosted $/image   Break-even/mo
FLUX schnell    4090     1.6s      ~2,250           $0.30      $0.00013  $0.003           ~10k images
FLUX schnell    H100     1.0s      ~3,600           $2.50      $0.00069  $0.003           ~22k images
FLUX dev (NF4)  4090     9s        ~400             $0.30      $0.00075  $0.025           ~12k images
FLUX dev        H100     5.5s      ~654             $2.50      $0.00382  $0.025           ~33k images
SDXL Lightning  4090     0.6s      ~6,000           $0.30      $0.00005  $0.0008          ~30k images
SD3.5 Large     H100     5s        ~720             $2.50      $0.00347  $0.018           ~38k images`}</pre>

				<p>
					Read those break-even numbers carefully. They assume 100% GPU
					utilization, which is fiction. A real autoscaler runs at 35–55% unless
					traffic is constant. Roughly double the break-even volume to get a
					realistic threshold: ~30–40k images per month for FLUX-class workloads
					before self-hosting saves money. Below that line, hosted inference
					wins on cost, on operational burden, and on the fact that you do not
					need to keep an H100 warm at 03:00 for a flaky cron job.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Recommended agent setup
				</h2>
				<p>
					The strongest agent loop in 2026 uses two tiers. Tier one is a fast
					model (FLUX schnell or SDXL Lightning) for drafts, candidates, and
					anything the agent will iterate on. Tier two is a quality model (FLUX
					dev, SD3.5 Large, or Playground v3 for stylized work) for the final
					selected output. The agent decides per call. On AgentFramer this is
					one tool — the{" "}
					<Link
						to="/help/mcp-tools"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						generate_image MCP tool
					</Link>{" "}
					takes a model parameter and routes accordingly. See{" "}
					<Link
						to="/help/tool-call-patterns"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						tool call patterns
					</Link>{" "}
					for the draft-then-refine loop.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					When open-source is the wrong call
				</h2>
				<p>
					The honest version. Open weights are a tool, not a virtue. There are
					three situations where running them yourself is a worse decision than
					calling a hosted endpoint.
				</p>
				<ul className="list-disc pl-5 space-y-1">
					<li>
						<strong className="text-foreground">
							Under ~10k images per month.
						</strong>{" "}
						The break-even math does not work. A weekend project, an internal
						tool, a marketing experiment — pay per image, do not stand up a GPU.
					</li>
					<li>
						<strong className="text-foreground">No GPU ops on staff.</strong>{" "}
						CUDA driver pinning, OOM debugging, autoscaler tuning, queue
						saturation, model swap downtime. If nobody on the team wants that
						pager, hosted is the answer.
					</li>
					<li>
						<strong className="text-foreground">
							Latency-sensitive on small cards.
						</strong>{" "}
						An 8 GB card running FLUX dev quantized to NF4 takes 25+ seconds per
						image. That is a fine demo and a terrible production loop. If the
						agent is user-facing and waits on the result, rent an H100 or use
						the API.
					</li>
				</ul>

				<h2 className="text-base font-medium text-foreground pt-2">
					On AgentFramer
				</h2>
				<p>
					AgentFramer ships every model in the table above (plus the
					closed-source Ideogram v2 and Imagen 3) on the same MCP surface,
					billed per image, no GPU to manage. The agent picks the right model
					per call. Pricing details live on the{" "}
					<Link
						to="/pricing"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						pricing page
					</Link>
					. If you outgrow hosted, the same prompts and schedulers port cleanly
					to a self-hosted ComfyUI stack — that is the point of sticking to open
					weights even when someone else runs them for you.
				</p>

				<p>
					Pick the model that matches the workload, not the one that won last
					week's leaderboard. An agent running schnell at four steps on a steady
					scheduler will out-ship a careful human running whatever model Twitter
					is excited about. Steady beats clever every time the loop runs
					unattended.
				</p>
			</div>
		</article>
	);
}
